import React, { useState, useEffect, useContext } from 'react';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { Bell } from 'lucide-react';

import { FirebaseContext } from '../App';
import { firebaseAppId } from '../configs/firebaseConfig';

const SettingsPage = () => {
    const { db, userId } = useContext(FirebaseContext);
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('');

    useEffect(() => {
        if (!db || !userId) return;

        setLoading(true);
        const userSettingsDocRef = doc(db, `artifacts/${firebaseAppId}/users/${userId}/settings`, 'user_settings');
        const unsubscribe = onSnapshot(userSettingsDocRef, (docSnap) => {
            if (docSnap.exists()) {
                setSettings(docSnap.data());
            } else {
                setSettings({
                    notificationSettings: {
                        dailyReminder: { enabled: true, time: '10:00' },
                        weeklyReminder: { enabled: true, day: 1, time: '09:00' },
                        monthlyReminder: { enabled: true, date: 1, time: '09:30' },
                        taxFilingReminder: { enabled: true, preDeadline: true, deadline: true },
                        consumptionTaxReminder: { enabled: true, preDeadline: true, deadline: true },
                        incompleteTransactionReminder: { enabled: true }
                    }
                });
            }
            setLoading(false);
        }, (error) => {
            console.error("設定の取得エラー:", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [db, userId]);

    const handleSettingChange = (category, key, value) => {
        setSettings(prevSettings => ({
            ...prevSettings,
            notificationSettings: {
                ...prevSettings.notificationSettings,
                [category]: {
                    ...prevSettings.notificationSettings[category],
                    [key]: value
                }
            }
        }));
    };

    const handleSaveSettings = async () => {
        if (!db || !userId || !settings) return;

        setMessage('');
        try {
            const userSettingsDocRef = doc(db, `artifacts/${firebaseAppId}/users/${userId}/settings`, 'user_settings');
            await setDoc(userSettingsDocRef, settings, { merge: true });
            setMessage('設定が保存されました！');
            setMessageType('success');
            setTimeout(() => setMessage(''), 3000);
        } catch (err) {
            console.error("設定の保存エラー:", err);
            setMessage('設定の保存中にエラーが発生しました: ' + err.message);
            setMessageType('error');
        }
    };

    if (loading) {
        return (
            <div className="bg-white rounded-xl shadow-lg p-8 text-center text-gray-600">
                設定を読み込み中...
            </div>
        );
    }

    if (!settings) {
        return (
            <div className="bg-white rounded-xl shadow-lg p-8 text-center text-red-600">
                設定を読み込めませんでした。
            </div>
        );
    }

    const ns = settings.notificationSettings;

    return (
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-2xl mx-auto">
            <h2 className="text-3xl font-semibold text-gray-800 mb-6 border-b pb-4">設定</h2>

            {message && (
                <div className={`p-4 rounded-md mb-4 ${messageType === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {message}
                </div>
            )}

            <div className="space-y-8">
                {/* 定期的なリマインダー */}
                <div>
                    <h3 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <Bell size={22} className="text-blue-500" />
                        定期的なリマインダー
                    </h3>
                    <div className="space-y-4 p-4 border rounded-lg bg-gray-50">
                        <div className="flex items-center justify-between">
                            <label className="text-gray-700 font-medium">日次リマインダー</label>
                            <input
                                type="checkbox"
                                checked={ns.dailyReminder.enabled}
                                onChange={(e) => handleSettingChange('dailyReminder', 'enabled', e.target.checked)}
                                className="form-checkbox h-5 w-5 text-blue-600 rounded"
                            />
                        </div>
                        {ns.dailyReminder.enabled && (
                            <div className="ml-6">
                                <label className="block text-gray-700 text-sm mb-1">時間</label>
                                <input
                                    type="time"
                                    value={ns.dailyReminder.time}
                                    onChange={(e) => handleSettingChange('dailyReminder', 'time', e.target.value)}
                                    className="shadow border rounded-lg py-2 px-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                />
                            </div>
                        )}

                        <div className="flex items-center justify-between">
                            <label className="text-gray-700 font-medium">週次リマインダー</label>
                            <input
                                type="checkbox"
                                checked={ns.weeklyReminder.enabled}
                                onChange={(e) => handleSettingChange('weeklyReminder', 'enabled', e.target.checked)}
                                className="form-checkbox h-5 w-5 text-blue-600 rounded"
                            />
                        </div>
                        {ns.weeklyReminder.enabled && (
                            <div className="ml-6 space-y-2">
                                <div>
                                    <label className="block text-gray-700 text-sm mb-1">曜日</label>
                                    <select
                                        value={ns.weeklyReminder.day}
                                        onChange={(e) => handleSettingChange('weeklyReminder', 'day', Number(e.target.value))}
                                        className="shadow border rounded-lg py-2 px-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    >
                                        <option value={0}>日曜日</option>
                                        <option value={1}>月曜日</option>
                                        <option value={2}>火曜日</option>
                                        <option value={3}>水曜日</option>
                                        <option value={4}>木曜日</option>
                                        <option value={5}>金曜日</option>
                                        <option value={6}>土曜日</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-gray-700 text-sm mb-1">時間</label>
                                    <input
                                        type="time"
                                        value={ns.weeklyReminder.time}
                                        onChange={(e) => handleSettingChange('weeklyReminder', 'time', e.target.value)}
                                        className="shadow border rounded-lg py-2 px-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    />
                                </div>
                            </div>
                        )}

                        <div className="flex items-center justify-between">
                            <label className="text-gray-700 font-medium">月次リマインダー</label>
                            <input
                                type="checkbox"
                                checked={ns.monthlyReminder.enabled}
                                onChange={(e) => handleSettingChange('monthlyReminder', 'enabled', e.target.checked)}
                                className="form-checkbox h-5 w-5 text-blue-600 rounded"
                            />
                        </div>
                        {ns.monthlyReminder.enabled && (
                            <div className="ml-6 space-y-2">
                                <div>
                                    <label className="block text-gray-700 text-sm mb-1">日付</label>
                                    <input
                                        type="number"
                                        value={ns.monthlyReminder.date}
                                        onChange={(e) => handleSettingChange('monthlyReminder', 'date', Number(e.target.value))}
                                        min="1"
                                        max="31"
                                        className="shadow border rounded-lg py-2 px-3 text-gray-700 w-20 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    /> 日
                                </div>
                                <div>
                                    <label className="block text-gray-700 text-sm mb-1">時間</label>
                                    <input
                                        type="time"
                                        value={ns.monthlyReminder.time}
                                        onChange={(e) => handleSettingChange('monthlyReminder', 'time', e.target.value)}
                                        className="shadow border rounded-lg py-2 px-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* 特定の期日に合わせた通知 */}
                <div>
                    <h3 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <Bell size={22} className="text-yellow-500" />
                        期日通知 (確定申告など)
                    </h3>
                    <div className="space-y-4 p-4 border rounded-lg bg-gray-50">
                        <div className="flex items-center justify-between">
                            <label className="text-gray-700 font-medium">確定申告通知</label>
                            <input
                                type="checkbox"
                                checked={ns.taxFilingReminder.enabled}
                                onChange={(e) => handleSettingChange('taxFilingReminder', 'enabled', e.target.checked)}
                                className="form-checkbox h-5 w-5 text-blue-600 rounded"
                            />
                        </div>
                        {ns.taxFilingReminder.enabled && (
                            <div className="ml-6 space-y-2">
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={ns.taxFilingReminder.preDeadline}
                                        onChange={(e) => handleSettingChange('taxFilingReminder', 'preDeadline', e.target.checked)}
                                        className="form-checkbox h-4 w-4 text-blue-600 rounded mr-2"
                                    />
                                    <label className="text-gray-700 text-sm">事前通知 (準備期間)</label>
                                </div>
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={ns.taxFilingReminder.deadline}
                                        onChange={(e) => handleSettingChange('taxFilingReminder', 'deadline', e.target.checked)}
                                        className="form-checkbox h-4 w-4 text-blue-600 rounded mr-2"
                                    />
                                    <label className="text-gray-700 text-sm">期限間近・当日通知</label>
                                </div>
                            </div>
                        )}

                        <div className="flex items-center justify-between">
                            <label className="text-gray-700 font-medium">消費税申告通知</label>
                            <input
                                type="checkbox"
                                checked={ns.consumptionTaxReminder.enabled}
                                onChange={(e) => handleSettingChange('consumptionTaxReminder', 'enabled', e.target.checked)}
                                className="form-checkbox h-5 w-5 text-blue-600 rounded"
                            />
                        </div>
                        {ns.consumptionTaxReminder.enabled && (
                            <div className="ml-6 space-y-2">
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={ns.consumptionTaxReminder.preDeadline}
                                        onChange={(e) => handleSettingChange('consumptionTaxReminder', 'preDeadline', e.target.checked)}
                                        className="form-checkbox h-4 w-4 text-blue-600 rounded mr-2"
                                    />
                                    <label className="text-gray-700 text-sm">事前通知 (準備期間)</label>
                                </div>
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={ns.consumptionTaxReminder.deadline}
                                        onChange={(e) => handleSettingChange('consumptionTaxReminder', 'deadline', e.target.checked)}
                                        className="form-checkbox h-4 w-4 text-blue-600 rounded mr-2"
                                    />
                                    <label className="text-gray-700 text-sm">期限間近・当日通知</label>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* 不備や未完了事項に関する通知 */}
                <div>
                    <h3 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <Bell size={22} className="text-red-500" />
                        不備・未完了通知
                    </h3>
                    <div className="space-y-4 p-4 border rounded-lg bg-gray-50">
                        <div className="flex items-center justify-between">
                            <label className="text-gray-700 font-medium">未入力取引リマインダー</label>
                            <input
                                type="checkbox"
                                checked={ns.incompleteTransactionReminder.enabled}
                                onChange={(e) => handleSettingChange('incompleteTransactionReminder', 'enabled', e.target.checked)}
                                className="form-checkbox h-5 w-5 text-blue-600 rounded"
                            />
                        </div>
                    </div>
                </div>

                <button
                    onClick={handleSaveSettings}
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-4 rounded-lg focus:outline-none focus:shadow-outline transition duration-200 ease-in-out shadow-md"
                >
                    設定を保存
                </button>
            </div>
        </div>
    );
};

export default SettingsPage;
